import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Copy,
  Download,
  Mail,
  Loader2,
  Check,
  Eye,
  Code
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EmailCampaignView() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'html'

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  const loadCampaign = async () => {
    try {
      const response = await axios.get(`${API}/email-campaigns/${campaignId}`);
      setCampaign(response.data.campaign);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load campaign');
      navigate('/email-campaigns');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadAsHTML = () => {
    if (!campaign || !campaign.emails) return;

    let html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${campaign.offer_name} - Email Sequence</title>
  <style>
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
      background: #f5f5f5;
    }
    .email {
      background: white;
      border: 1px solid #ddd;
      border-radius: 8px;
      padding: 30px;
      margin-bottom: 30px;
    }
    .email-header {
      border-bottom: 2px solid #00d4aa;
      padding-bottom: 15px;
      margin-bottom: 20px;
    }
    .email-meta {
      color: #666;
      font-size: 14px;
      margin-bottom: 10px;
    }
    .subject {
      font-size: 20px;
      font-weight: bold;
      color: #333;
    }
    .preview {
      color: #666;
      font-style: italic;
      margin-top: 5px;
    }
    .body {
      white-space: pre-wrap;
      color: #333;
      margin: 20px 0;
    }
    .ps {
      border-top: 1px solid #eee;
      padding-top: 15px;
      margin-top: 20px;
      color: #666;
    }
  </style>
</head>
<body>
  <h1>${campaign.offer_name}</h1>
  <p><strong>Business:</strong> ${campaign.business_name}</p>
  <p><strong>Target Audience:</strong> ${campaign.target_audience}</p>
  <p><strong>Awareness Level:</strong> ${campaign.awareness_level}</p>
  <hr>
`;

    campaign.emails.forEach((email) => {
      html += `
  <div class="email">
    <div class="email-header">
      <div class="email-meta">Email ${email.email_number} - ${email.email_type}</div>
      <div class="subject">${email.subject_line}</div>
      <div class="preview">${email.preview_text}</div>
    </div>
    <div class="body">${email.body}</div>
    <div class="ps"><strong>PS:</strong> ${email.ps_line}</div>
  </div>
`;
    });

    html += `
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaign.offer_name.replace(/\s+/g, '_')}_Email_Sequence.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded email sequence');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  const currentEmail = campaign.emails[selectedEmail];

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/email-campaigns')}
            className="mb-4 text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Campaigns
          </Button>
          <h1 className="text-3xl font-bold text-white mb-2">
            {campaign.offer_name}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-white/60">
            <span>{campaign.business_name}</span>
            <span>•</span>
            <span>{campaign.emails.length} emails</span>
            <span>•</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
              {campaign.awareness_level}
            </span>
          </div>
        </div>
        <Button
          onClick={downloadAsHTML}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
        >
          <Download className="w-4 h-4 mr-2" />
          Download HTML
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Email List */}
        <div className="lg:col-span-1">
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-lg">Email Sequence</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {campaign.emails.map((email, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedEmail(idx)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    selectedEmail === idx
                      ? 'bg-[#00d4aa]/20 border border-[#00d4aa]/50'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-[#00d4aa]" />
                    <span className="font-semibold text-white">Email {email.email_number}</span>
                  </div>
                  <div className="text-xs text-white/60">{email.email_type}</div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Email Content */}
        <div className="lg:col-span-3">
          <Card className="card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Email {currentEmail.email_number}: {currentEmail.email_type}</CardTitle>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setViewMode(viewMode === 'preview' ? 'html' : 'preview')}
                  >
                    {viewMode === 'preview' ? <Code className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Subject Line */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-sm font-medium">Subject Line</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(currentEmail.subject_line, 'subject')}
                  >
                    {copiedId === 'subject' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white font-medium">{currentEmail.subject_line}</p>
                </div>
              </div>

              {/* Preview Text */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-sm font-medium">Preview Text</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(currentEmail.preview_text, 'preview')}
                  >
                    {copiedId === 'preview' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white/70 text-sm italic">{currentEmail.preview_text}</p>
                </div>
              </div>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-sm font-medium">Email Body</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(currentEmail.body, 'body')}
                  >
                    {copiedId === 'body' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white whitespace-pre-wrap">{currentEmail.body}</p>
                </div>
              </div>

              {/* PS Line */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-sm font-medium">PS Line</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(currentEmail.ps_line, 'ps')}
                  >
                    {copiedId === 'ps' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white">
                    <strong>PS:</strong> {currentEmail.ps_line}
                  </p>
                </div>
              </div>

              {/* Copy Full Email */}
              <Button
                onClick={() => {
                  const fullEmail = `Subject: ${currentEmail.subject_line}\n\nPreview: ${currentEmail.preview_text}\n\n${currentEmail.body}\n\nPS: ${currentEmail.ps_line}`;
                  copyToClipboard(fullEmail, 'full');
                }}
                variant="outline"
                className="w-full"
              >
                {copiedId === 'full' ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-400" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Full Email
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default EmailCampaignView;
